/**
 * @ccheng/api-client
 *
 * 呼叫 FastAPI（api/，埠 8100）的前端 client。
 * 對應規格：
 *  - 認證 API 契約：api/app/api/v1/auth.py（POST /api/v1/auth/login、GET /api/v1/me）
 *  - ListResponse 協定：api/app/api/schemas.py（心成Firestore資料模型-最終版.md §9）
 */

import { ApiError, mapErrorMessage, NETWORK_ERROR_MESSAGE } from "./errors";
import { clearToken, getToken, setToken } from "./token";

export { ApiError, mapErrorMessage, NETWORK_ERROR_MESSAGE } from "./errors";
export { clearToken, getToken, setToken } from "./token";

// ---- 共用清單回應協定（api/app/api/schemas.py ListResponse，全站清單頁共用）----
export interface ListResponse<T = Record<string, unknown>> {
  items: T[];
  nextCursor: string | null;
  hasMore: boolean;
  total?: number | null;
  asOf?: string | null;
  degraded?: boolean | null;
}

// ---- 認證 API 契約（api/app/api/v1/auth.py）----
export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  tokenType: string;
  expiresInMinutes: number;
}

export interface MeResponse {
  id: string;
  username: string;
  email: string | null;
  roles: string[];
  isSuperAdministrator: boolean;
  permissions: string[];
}

// ---- baseURL 設定：預設 127.0.0.1:8100，可由 env 或 configureApiClient() 覆寫 ----
function readEnvBaseUrl(): string | undefined {
  try {
    // Next.js 於 build 期以字面字串替換 `process.env.NEXT_PUBLIC_*`；
    // 在非瀏覽器打包環境（例如純 Node 測試）process 可能不存在，故整段包在 try 內。
    return process.env.NEXT_PUBLIC_CCHENG_API_BASE_URL;
  } catch {
    return undefined;
  }
}

let baseUrl = readEnvBaseUrl()?.trim() || "http://127.0.0.1:8100";
let onUnauthorized: (() => void) | undefined;

export interface ApiClientConfig {
  /** 覆寫 API base URL（預設 http://127.0.0.1:8100，或 NEXT_PUBLIC_CCHENG_API_BASE_URL）。 */
  baseUrl?: string;
  /** 任何請求收到 401 時呼叫（除了本身已清除 token 外，供消費端導回登入頁）。 */
  onUnauthorized?: () => void;
}

export function configureApiClient(config: ApiClientConfig): void {
  if (config.baseUrl) baseUrl = config.baseUrl;
  if (config.onUnauthorized) onUnauthorized = config.onUnauthorized;
}

export function getApiBaseUrl(): string {
  return baseUrl;
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  /** true（預設）：帶 Authorization: Bearer <token>；401 時觸發 onUnauthorized。 */
  auth?: boolean;
  body?: unknown;
}

/** 泛用 request：JSON in / JSON out，401 導回登入、非 2xx 一律拋 ApiError（訊息為 zh）。 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { auth = true, headers, body, ...rest } = options;
  const finalHeaders: Record<string, string> = {
    Accept: "application/json",
    ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    ...(headers as Record<string, string> | undefined),
  };

  if (auth) {
    const token = getToken();
    if (token) finalHeaders.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${baseUrl}${path}`, {
      ...rest,
      headers: finalHeaders,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    // fetch 本身失敗：網路中斷、DNS、或 CORS 被瀏覽器擋下（後者 fetch 只會 reject，
    // 讀不到真正狀態碼）——status 用 0 代表「非 HTTP 層級錯誤」。
    throw new ApiError(NETWORK_ERROR_MESSAGE, 0, err);
  }

  if (response.status === 401 && auth) {
    clearToken();
    onUnauthorized?.();
  }

  if (!response.ok) {
    let responseBody: unknown;
    try {
      responseBody = await response.json();
    } catch {
      // 無 JSON body（例如 502/504 由 nginx 直接回的 HTML）——沿用狀態碼通用訊息。
    }
    throw new ApiError(mapErrorMessage(response.status, responseBody), response.status, responseBody);
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export const apiClient = {
  get: <T>(path: string, options?: RequestOptions) => apiRequest<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: "POST", body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: "PUT", body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: "PATCH", body }),
  delete: <T>(path: string, options?: RequestOptions) => apiRequest<T>(path, { ...options, method: "DELETE" }),
};

// ---- 認證 ----
export const authApi = {
  async login(body: LoginRequest): Promise<LoginResponse> {
    const res = await apiRequest<LoginResponse>("/api/v1/auth/login", {
      method: "POST",
      body,
      auth: false,
    });
    setToken(res.accessToken);
    return res;
  },
  me(): Promise<MeResponse> {
    return apiRequest<MeResponse>("/api/v1/me", { method: "GET" });
  },
  logout(): void {
    clearToken();
  },
};
